import { Controller } from '@hotwired/stimulus';

// Per-tab, and Chrome opens a fresh tab per uninstall, so this is the removal's own scope.
const UNINSTALL_COUNTED_KEY = 'unscreen_extension_uninstall_counted';

// The button ships enabled so the form still works without JS; connect() is what disables it.
export default class extends Controller {
  static targets = ['submit', 'feedback', 'distinctId'];
  static values = { uninstalledEvent: String };

  connect() {
    // First: the uninstall is the one thing this page exists to record, so nothing below can cost it.
    this.#captureUninstall();
    this.refresh();
    this.#stampDistinctId();
    // A back-navigation restores this page from the bfcache without re-running connect(), so a button
    // #lock disabled would stay disabled.
    this.restore = () => this.refresh();
    window.addEventListener('pageshow', this.restore);
  }

  disconnect() {
    window.removeEventListener('pageshow', this.restore);
  }

  refresh() {
    this.submitTarget.disabled = !(this.#hasReason() || this.#hasFeedback());
  }

  // invisible_captcha consumes its session timestamp on the first POST, so a second one is read as spam
  // and silently discarded — a double-click has to be stopped here, not sorted out server-side.
  lock(event) {
    if (this.submitTarget.disabled) {
      event.preventDefault();
      return;
    }

    this.#stampDistinctId();
    this.submitTarget.disabled = true;
  }

  // Page load is the only uninstall signal Chrome gives us, and this is the only element guaranteed
  // to be on that page. Counted once per tab: a rejected submission re-renders this page, and a reload or
  // a back-navigation re-runs connect(), all of which would report the same removal again.
  #captureUninstall() {
    if (!this.uninstalledEventValue) return;
    // An analytics-excluded account renders no snippet, and a claim spent on that load silences the tab (UNS-5173).
    if (!window.posthog?.capture || !this.#claimUninstall()) return;

    window.posthog.capture(this.uninstalledEventValue);
  }

  // Storage throws where it's blocked; counting a removal twice beats never counting it, so a throw claims.
  #claimUninstall() {
    try {
      if (sessionStorage.getItem(UNINSTALL_COUNTED_KEY)) return false;
      sessionStorage.setItem(UNINSTALL_COUNTED_KEY, '1');
    } catch {
      // See above.
    }

    return true;
  }

  // Keys the server's reason event to the same anonymous id as the uninstall event above — without it the
  // two can't be joined. Stamped again on submit because the snippet may still be a stub at connect().
  #stampDistinctId() {
    if (!this.hasDistinctIdTarget) return;

    const distinctId = window.posthog?.get_distinct_id?.();
    if (distinctId) this.distinctIdTarget.value = distinctId;
  }

  #hasReason() {
    return Boolean(this.element.querySelector('input[name="reason"]:checked'));
  }

  #hasFeedback() {
    return this.hasFeedbackTarget && this.feedbackTarget.value.trim().length > 0;
  }
}
